/* Shopify theme-only frequently bought together. No Admin token or server required. */
(() => {
  'use strict';
  const roots = new WeakMap();
  const rootPath = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const api = (path) => rootPath().replace(/\/?$/, '/') + path;

  async function json(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  function setStatus(root, text) {
    const status = root.querySelector('[data-fbt-status]');
    if (!status) return;
    status.textContent = text;
    status.hidden = !text;
  }

  async function build(root) {
    if (!root.isConnected) return;
    const state = roots.get(root);
    if (!state) return;
    const job = ++state.job;
    const grid = root.querySelector('[data-fbt-grid]');
    if (!grid) return;
    grid.replaceChildren();
    setStatus(root, '');

    try {
      const cart = await json(api('cart.js'));
      if (job !== state.job || !root.isConnected) return;
      const productIds = [...new Set(cart.items.map(item => String(item.product_id)))];
      if (!productIds.length) {
        root.hidden = true;
        return;
      }
      const sources = productIds.slice(0, Number(root.dataset.maxSources) || 10);
      const cartIds = new Set(productIds);
      const intent = root.dataset.intent === 'complementary' ? 'complementary' : 'related';
      const section = root.dataset.recommendationSection || 'fbt-candidates';

      const batches = await Promise.all(sources.map(async (sourceId) => {
        const params = new URLSearchParams({
          product_id: sourceId,
          section_id: section,
          limit: '10',
          intent
        });
        try {
          const response = await fetch(api('recommendations/products') + '?' + params, {
            credentials: 'same-origin'
          });
          if (!response.ok) throw new Error(`Recommendations HTTP ${response.status}`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return [...parsed.querySelectorAll('.fbt-candidate-response [data-fbt-product-id]')];
        } catch (error) {
          console.warn('[FBT] Recommendation request failed', sourceId, error);
          return [];
        }
      }));
      if (job !== state.job || !root.isConnected) return;

      const candidates = new Map();
      batches.forEach((batch, sourceIndex) => {
        batch.forEach((card, position) => {
          const id = card.dataset.fbtProductId;
          if (!id || cartIds.has(id)) return;
          // Each distinct cart source counts at most once per candidate.
          const previous = candidates.get(id);
          if (previous) {
            if (previous.sources.has(sourceIndex)) return;
            previous.sources.add(sourceIndex);
            previous.positionScore += (10 - position);
          } else {
            candidates.set(id, {
              node: card,
              sources: new Set([sourceIndex]),
              positionScore: 10 - position
            });
          }
        });
      });
      const limit = Number(root.dataset.limit) || 3;
      const ordered = [...candidates.values()]
        .sort((a,b) => b.sources.size - a.sources.size || b.positionScore - a.positionScore)
        .slice(0, limit);

      // Server-rendered Liquid candidates have already passed MA exclusion.
      ordered.forEach(({ node }) => grid.appendChild(document.importNode(node, true)));
      root.hidden = ordered.length === 0;
      viewportReset(root);
      updateSlider(root);
    } catch (error) {
      root.hidden = true;
      console.warn('[FBT] Cart recommendation build failed', error);
    }
  }

  function itemsPerPage(root) {
    return root.classList.contains('fbt-root--drawer') ? 1 : (window.matchMedia('(max-width: 749px)').matches ? 2 : 6);
  }

  function viewportReset(root) {
    const viewport = root.querySelector('[data-fbt-viewport]');
    if (viewport) viewport.scrollLeft = 0;
  }

  function slideWidth(root) {
    const viewport = root.querySelector('[data-fbt-viewport]');
    return viewport ? viewport.clientWidth : 0;
  }

  function updateSlider(root) {
    const viewport = root.querySelector('[data-fbt-viewport]');
    const count = root.querySelectorAll('[data-fbt-grid] > .fbt-card').length;
    if (!viewport) return;
    const perPage = itemsPerPage(root);
    const pages = Math.ceil(count / perPage);
    const page = Math.min(Math.max(0, Math.round(viewport.scrollLeft / Math.max(slideWidth(root), 1))), Math.max(0, pages - 1));
    if (root.classList.contains('fbt-root--drawer')) {
      const dots = root.querySelector('[data-fbt-dots]');
      if (dots && dots.childElementCount !== count) {
        dots.replaceChildren();
        for (let i = 0; i < count; i++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fbt-dot';
          button.dataset.fbtPage = String(i);
          button.setAttribute('aria-label', `Show recommendation ${i + 1} of ${count}`);
          dots.appendChild(button);
        }
      }
      dots?.querySelectorAll('[data-fbt-page]').forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
    } else {
      const controls = root.querySelector('[data-fbt-controls]');
      if (controls) controls.hidden = pages < 2;
      const prev = root.querySelector('[data-fbt-prev]');
      const next = root.querySelector('[data-fbt-next]');
      if (prev) prev.disabled = page === 0;
      if (next) next.disabled = page >= pages - 1;
    }
  }

  function goToPage(root, requestedPage) {
    const viewport = root.querySelector('[data-fbt-viewport]');
    const count = root.querySelectorAll('[data-fbt-grid] > .fbt-card').length;
    if (!viewport || count < 2) return;
    const max = Math.max(0, Math.ceil(count / itemsPerPage(root)) - 1);
    const page = Math.max(0, Math.min(max, requestedPage));
    viewport.scrollTo({ left: page * viewport.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  function moveSlide(root, direction) {
    const viewport = root.querySelector('[data-fbt-viewport]');
    if (!viewport) return;
    const page = Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1));
    goToPage(root, page + direction);
  }

  async function addProduct(event, root) {
    const button = event.target.closest('[data-fbt-add]');
    if (!button || !root.contains(button) || button.disabled) return;
    const id = Number(button.dataset.variantId);
    if (!Number.isSafeInteger(id)) return;
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';
    try {
      const drawer = button.closest('cart-drawer');
      // Dawn's cart drawer can re-render its own sections without reloading the page.
      const sections = drawer ? ['cart-drawer', 'cart-icon-bubble'] : [];
      const data = await json(api('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id, quantity: 1 }],
          ...(sections.length ? { sections, sections_url: window.location.pathname } : {})
        })
      });
      button.textContent = 'Added';
      if (drawer && typeof drawer.renderContents === 'function' &&
          data.sections && data.sections['cart-drawer']) {
        // Dawn's renderContents expects the cart-add response, which has sections and id.
        drawer.renderContents(data);
        document.dispatchEvent(new Event('fbt:refresh'));
      } else {
        // Reliable Dawn cart-page fallback for cart items, prices and totals.
        window.location.reload();
      }
      document.dispatchEvent(new CustomEvent('fbt:product-added', { detail: { variantId: id } }));
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      setStatus(root, 'Could not add this product. Please try again.');
      console.error('[FBT] Add to cart failed', error);
    }
  }

  function init(root) {
    if (roots.has(root)) return;
    roots.set(root, { job: 0, timer: null });
    root.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-fbt-page]');
      if (dot) { goToPage(root, Number(dot.dataset.fbtPage)); return; }
      if (event.target.closest('[data-fbt-prev]')) { moveSlide(root, -1); return; }
      if (event.target.closest('[data-fbt-next]')) { moveSlide(root, 1); return; }
      addProduct(event, root);
    });
    {
      const viewport = root.querySelector('[data-fbt-viewport]');
      viewport?.addEventListener('scroll', () => updateSlider(root), { passive: true });
      window.addEventListener('resize', () => updateSlider(root));
    }
    if (root.classList.contains('fbt-root--drawer')) {
      const interval = Math.max(0, Number(root.dataset.fbtAutoplay) || 0);
      if (interval >= 2000) {
        const timer = setInterval(() => {
          if (!root.isConnected) { clearInterval(timer); return; }
          if (root.hidden || document.hidden || !root.closest('cart-drawer')?.classList.contains('active')) return;
          if (root.matches(':hover') || root.contains(document.activeElement)) return;
          const slides = root.querySelectorAll('[data-fbt-grid] > .fbt-card').length;
          if (slides < 2) return;
          const viewport = root.querySelector('[data-fbt-viewport]');
          const index = Math.round(viewport.scrollLeft / Math.max(viewport.clientWidth, 1));
          goToPage(root, (index + 1) % slides);
        }, interval);
      }
    }
    refresh(root);
  }

  function refresh(root) {
    const state = roots.get(root);
    if (!state) return;
    clearTimeout(state.timer);
    state.timer = setTimeout(() => build(root), Number(root.dataset.refreshDelay) || 350);
  }

  function initAll() { document.querySelectorAll('[data-fbt-root]').forEach(init); }
  document.addEventListener('DOMContentLoaded', initAll);
  if (document.readyState !== 'loading') initAll();
  document.addEventListener('shopify:section:load', initAll);
  // Other theme code may dispatch one of these after cart changes.
  ['cart:updated', 'cart:refresh', 'cart:change', 'fbt:refresh'].forEach(name => {
    document.addEventListener(name, () => document.querySelectorAll('[data-fbt-root]').forEach(refresh));
  });
  // When a theme dynamically replaces cart markup, initialize the new root.
  const observer = new MutationObserver((mutations) => {
    if (mutations.some(m => [...m.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-fbt-root]') || n.querySelector?.('[data-fbt-root]'))))) initAll();
  });
  const startObserver = () => observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) startObserver();
  else document.addEventListener('DOMContentLoaded', startObserver, { once: true });
})();
